import cloneDeep from "lodash/cloneDeep";
import { v4 as uuidv4 } from "uuid";
import config from "../../../config";
import { UTP2CustomQuestionTypes, UTP2Groups, UTP2Substations } from "../../shared/enums/UTP2";
import type { CustomQuestionField } from "../../shared/interfaces/Workflow";
import type { WorkflowV1 } from "../../shared/interfaces/WorkflowV1";
import type { HvSurveyableAsset, WorkflowV2 } from "../../shared/interfaces/WorkflowV2";
import { emptyWorkflowV1, emptyWorkflowV2 } from "./defaults";
import {
  createWorkflowMetadataField,
  mapConditionsToMetadataFields,
  setWorkflowSurveyMetadataGroupFields,
  updateWorkflowCustomQuestionGroups,
  updateWorkflowSurveyMetadataGroups,
  upsertWorkflowSurveyMetadataField,
} from "./metadata";
import {
  clearAllMeasurementPanels,
  findDuplicateMeasurements,
  preserveMeasurementsOrder,
  rebuildMeasurementsOrder,
  rebuildOrderedMeasurementsOrder,
  setGroupedMeasurementsOrder,
} from "./measurementOrdering";
import { buildWorkflowWithMeasurements, generateWorkflowState } from "./state";
import type { GroupedMeasurement, GroupedPanel, WorkflowAction, WorkflowState, WorkflowType } from "./types";

export function workflowReducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
  switch (action.type) {
    case "setWorkflowFromObject":
      return generateWorkflowState(action.workflow, {
        preserveStoredMeasurementsOrder: false,
      });

    case "updateWorkflowField":
      return {
        ...state,
        workflow: state.workflow ? { ...state.workflow, [action.key]: action.value } : state.workflow,
      };

    case "toggleCondition": {
      const updatedConditions = state.conditions?.map((conditionItem) => ({
        key: conditionItem.key,
        checked: conditionItem.key === action.condition ? !conditionItem.checked : conditionItem.checked,
      }));

      return {
        ...state,
        workflow: setWorkflowSurveyMetadataGroupFields(
          state.workflow,
          UTP2Groups.Conditions,
          mapConditionsToMetadataFields(updatedConditions),
        ),
        conditions: updatedConditions,
      };
    }

    case "updateCableNaming":
      return {
        ...state,
        cableNaming: action.value,
        workflow: upsertWorkflowSurveyMetadataField(
          state.workflow,
          UTP2Groups.Substation,
          createWorkflowMetadataField(
            UTP2Substations.CableNaming,
            UTP2CustomQuestionTypes.Native,
            action.value.toString(),
          ),
        ),
      };

    case "updateWorkflowType": {
      if (action.workflowType === "generic") {
        if (state.workflow.workflowDetail.version === 1) {
          return applyGroupedMeasurementsToState(state, clearAllMeasurementPanels(state.groupedMeasurements), {
            workflowType: action.workflowType,
            detailOverrides: {
              substation_id: undefined,
              switchgear_id: undefined,
            },
            stateOverrides: {
              workflowType: action.workflowType,
            },
          });
        }

        return applyGroupedMeasurementsToState(state, [], {
          workflowType: action.workflowType,
          detailOverrides: {
            substation_id: undefined,
            switchgear_id: undefined,
          },
          stateOverrides: {
            workflowType: action.workflowType,
          },
        });
      }

      return {
        ...state,
        workflowType: action.workflowType,
      };
    }

    case "clearSubstationSwitchgear":
      return applyGroupedMeasurementsToState(state, clearAllMeasurementPanels(state.groupedMeasurements), {
        detailOverrides: {
          substation_id: undefined,
          switchgear_id: undefined,
        },
      });

    case "updateSubstationId":
      return applyGroupedMeasurementsToState(state, clearAllMeasurementPanels(state.groupedMeasurements), {
        detailOverrides: {
          substation_id: action.substationId,
          switchgear_id: undefined,
        },
      });

    case "updateSwitchgearId":
      return applyGroupedMeasurementsToState(state, clearAllMeasurementPanels(state.groupedMeasurements), {
        detailOverrides: {
          switchgear_id: action.switchgearId,
        },
        transformV2Assets: (assets) =>
          createOrUpdateSwitchgearAsset(assets, action.switchgearId, action.switchgearName, action.assetType),
      });

    case "updateSingleGroupedMeasurement": {
      const changedGroupMeasurements = cloneDeep(state.groupedMeasurements);
      const foundIndex = changedGroupMeasurements.findIndex(
        (measurement) => measurement.groupId === action.updatedMeasurementGroup.groupId,
      );

      if (foundIndex < 0) {
        return state;
      }

      changedGroupMeasurements[foundIndex] = action.updatedMeasurementGroup;

      return applyGroupedMeasurementsToState(state, changedGroupMeasurements, {
        orderMode: "preserve",
        duplicateMeasurementGroupIds: findDuplicateMeasurements(changedGroupMeasurements),
      });
    }

    case "updateAllGroupedMeasurements":
      return applyGroupedMeasurementsToState(state, action.groupedMeasurements, {
        orderMode: "preserve",
      });

    case "addNewMeasurementGroup": {
      const updatedMeasurements = [...state.groupedMeasurements];

      if (
        !updatedMeasurements.some(
          (measurement) =>
            !measurement.type &&
            !measurement.accessory &&
            !measurement.access_location &&
            !measurement.component &&
            !measurement.subloc,
        )
      ) {
        updatedMeasurements.push({
          groupId: `group-${uuidv4()}`,
          panels: [] as GroupedPanel[],
        } as GroupedMeasurement);
      }

      const orderPreservedState = applyGroupedMeasurementsToState(state, state.groupedMeasurements, {
        orderMode: "preserve",
      });

      return {
        ...orderPreservedState,
        groupedMeasurements: updatedMeasurements,
        workflow: buildWorkflowWithMeasurements(
          orderPreservedState.workflow,
          updatedMeasurements,
          orderPreservedState.workflowType,
        ),
      };
    }

    case "resetWorkflow":
      return generateWorkflowState(
        config.flexiSurveysEnabled ? (emptyWorkflowV2 as WorkflowV2) : (emptyWorkflowV1 as WorkflowV1),
      );

    case "resetMeasurementsOrder":
      return applyGroupedMeasurementsToState(state, state.groupedMeasurements, {
        orderMode: "normalise",
      });

    case "addCustomQuestionGroup": {
      const updatedCustomQuestionsGroups = cloneDeep(state.customQuestionsGroups);

      updatedCustomQuestionsGroups.push({
        name: action.customQuestionGroup?.name || "",
        id: uuidv4(),
        fields: action.customQuestionGroup?.fields || [],
      });

      return applyCustomQuestionGroups(state, updatedCustomQuestionsGroups);
    }

    case "removeCustomQuestionGroup":
      return applyCustomQuestionGroups(
        state,
        state.customQuestionsGroups.filter((group) => group.id !== action.groupId),
      );

    case "editCustomQuestionGroupName": {
      const updatedCustomQuestionsGroups = cloneDeep(state.customQuestionsGroups);
      const foundGroup = updatedCustomQuestionsGroups.find((group) => group.id === action.groupId);

      if (!foundGroup) {
        return state;
      }

      foundGroup.name = action.groupName;

      return applyCustomQuestionGroups(state, updatedCustomQuestionsGroups);
    }

    case "addCustomQuestionField": {
      const updatedCustomQuestionsGroups = cloneDeep(state.customQuestionsGroups);
      const foundGroup = updatedCustomQuestionsGroups.find((group) => group.id === action.groupId);

      if (!foundGroup) {
        return state;
      }

      foundGroup.fields.push({ ...action.fieldDetails, id: uuidv4() });

      return applyCustomQuestionGroups(state, updatedCustomQuestionsGroups);
    }

    case "editCustomQuestionField": {
      const updatedCustomQuestionsGroups = cloneDeep(state.customQuestionsGroups);
      const foundGroup = updatedCustomQuestionsGroups.find((group) => group.id === action.groupId);

      if (!foundGroup) {
        return state;
      }

      const foundFieldIndex = foundGroup.fields.findIndex(
        (field: CustomQuestionField) => field.id && field.id === action.fieldDetails.id,
      );

      if (foundFieldIndex < 0) {
        return state;
      }

      foundGroup.fields[foundFieldIndex] = action.fieldDetails;

      return applyCustomQuestionGroups(state, updatedCustomQuestionsGroups);
    }

    case "removeCustomQuestionField": {
      const updatedCustomQuestionsGroups = cloneDeep(state.customQuestionsGroups);
      const foundGroup = updatedCustomQuestionsGroups.find((group) => group.id === action.groupId);

      if (!foundGroup) {
        return state;
      }

      foundGroup.fields = foundGroup.fields.filter((field: CustomQuestionField) => field.id !== action.fieldId);

      return applyCustomQuestionGroups(state, updatedCustomQuestionsGroups);
    }

    case "updateAllOrderedMeasurements": {
      const orderedMeasurements = rebuildOrderedMeasurementsOrder(action.orderedMeasurements);
      const groupedMeasurements = setGroupedMeasurementsOrder(orderedMeasurements, state.groupedMeasurements);

      return {
        ...state,
        groupedMeasurements,
        orderedMeasurements,
        workflow: buildWorkflowWithMeasurements(
          state.workflow,
          groupedMeasurements,
          state.workflowType,
          state.workflow.workflowDetail.version === 2 ? { asset_types: [] } : {},
        ),
      };
    }

    default:
      return state;
  }
}

interface ApplyGroupedMeasurementsOptions {
  workflowType?: WorkflowType;
  detailOverrides?: Partial<WorkflowV1["workflowDetail"]> | Partial<WorkflowV2["workflowDetail"]>;
  stateOverrides?: Partial<WorkflowState>;
  orderMode?: "normalise" | "preserve" | "skip";
  duplicateMeasurementGroupIds?: string[];
  transformV2Assets?: (assets: HvSurveyableAsset[]) => HvSurveyableAsset[];
}

function applyGroupedMeasurementsToState(
  state: WorkflowState,
  groupedMeasurements: GroupedMeasurement[],
  {
    workflowType = state.workflowType,
    detailOverrides = {},
    stateOverrides = {},
    orderMode = "preserve",
    duplicateMeasurementGroupIds,
    transformV2Assets,
  }: ApplyGroupedMeasurementsOptions = {},
): WorkflowState {
  const [nextGroupedMeasurements, nextOrderedMeasurements]: [
    GroupedMeasurement[],
    WorkflowState["orderedMeasurements"],
  ] =
    orderMode === "normalise"
      ? rebuildMeasurementsOrder(groupedMeasurements)
      : orderMode === "preserve"
        ? preserveMeasurementsOrder(groupedMeasurements)
        : [groupedMeasurements, state.orderedMeasurements];
  const nextDuplicateMeasurementGroupIds =
    duplicateMeasurementGroupIds ?? findDuplicateMeasurements(nextGroupedMeasurements);

  return {
    ...state,
    ...stateOverrides,
    groupedMeasurements: nextGroupedMeasurements,
    orderedMeasurements: nextOrderedMeasurements,
    duplicateMeasurementGroupIds: nextDuplicateMeasurementGroupIds,
    workflow: buildWorkflowWithMeasurements(
      state.workflow,
      nextGroupedMeasurements,
      workflowType,
      detailOverrides,
      transformV2Assets,
    ),
  };
}

function applyCustomQuestionGroups(
  state: WorkflowState,
  customQuestionsGroups: WorkflowState["customQuestionsGroups"],
): WorkflowState {
  const surveyMetadataGroups = updateWorkflowCustomQuestionGroups(state.workflow, customQuestionsGroups);

  return {
    ...state,
    workflow: updateWorkflowSurveyMetadataGroups(state.workflow, surveyMetadataGroups),
    customQuestionsGroups,
  };
}

function createOrUpdateSwitchgearAsset(
  assets: HvSurveyableAsset[],
  switchgearId?: number,
  switchgearName?: string,
  assetType?: string,
): HvSurveyableAsset[] {
  const switchgearAsset = {
    assetid: switchgearId,
    asset_type: assetType || "",
    name: switchgearName || "",
    sub_assets: [],
  } as HvSurveyableAsset;

  return assets.length > 0 ? [switchgearAsset, ...assets.slice(1)] : [switchgearAsset];
}
